import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Rocket, Mail, Lock, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { getAppUrl } from '../lib/utils';
import { savePendingInviteKey } from '../lib/pendingInvite';

const Login: React.FC = () => {
  const { signIn, signUp, verifyMfa } = useAuth();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [recoveryActive, setRecoveryActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<{ factorId: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  useEffect(() => {
    if (searchParams.get('mode') === 'signup') {
      setMode('signup');
    }
    const inviteKeyParam = searchParams.get('invite_key');
    if (inviteKeyParam) savePendingInviteKey(inviteKeyParam);
    if (searchParams.get('mode') === 'reset') {
      setMode('reset');
      supabase.auth.getSession().then(({ data }) => {
        if (data.session?.user) {
          setRecoveryActive(true);
          setInfo('Enter your new password below.');
        }
      });
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    if (mode === 'reset') {
      if (recoveryActive) {
        if (newPassword !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (newPassword.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        try {
          const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
          if (updateError) {
            setError(updateError.message);
          } else {
            setInfo('Password updated successfully. You can now sign in with your new password.');
            setRecoveryActive(false);
            setResetSent(false);
            setMode('signin');
            setPassword('');
            setNewPassword('');
            setConfirmPassword('');
            await supabase.auth.signOut();
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to update password');
        }
        setLoading(false);
        return;
      }

      if (!email) {
        setError('Enter your email address');
        setLoading(false);
        return;
      }
      try {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${getAppUrl()}/login?mode=reset`,
        });
        if (resetError) {
          setError(resetError.message);
        } else {
          setResetSent(true);
          setInfo('Check your inbox for a password reset link.');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send reset email');
      }
      setLoading(false);
      return;
    }

    if (mfaChallenge) {
      if (!mfaCode.trim()) {
        setError('Enter your 6-digit authenticator code');
        setLoading(false);
        return;
      }
      const { error: mfaError } = await verifyMfa(mfaChallenge.factorId, mfaCode.trim());
      setLoading(false);
      if (mfaError) {
        setError(mfaError);
      }
      return;
    }

    const result = mode === 'signin'
      ? await signIn(email, password)
      : await signUp(email, password);

    setLoading(false);

    if (result.error) {
      setError(result.error);
    } else if (result.mfaChallenge) {
      setMfaChallenge(result.mfaChallenge);
      setInfo('Enter the 6-digit code from your authenticator app.');
      setError(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-100 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary-100 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="glass-card p-8 animate-scale-in">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-gradient-brand rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-primary-500/20">
              <Rocket className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              Aurelian Marketing Engine
            </h1>
            <p className="text-sm text-slate-500 mt-2 font-mono">
              {mode === 'signin' ? 'Sign in to your command center' : mode === 'signup' ? 'Create your account' : 'Reset your password'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode !== 'reset' && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-dark w-full pl-11 pr-4 py-3 text-sm"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
            )}

            {mode === 'reset' && !recoveryActive && !resetSent && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-dark w-full pl-11 pr-4 py-3 text-sm"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
            )}

            {mode !== 'reset' && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="input-dark w-full pl-11 pr-4 py-3 text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            )}

            {mode === 'reset' && recoveryActive && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="input-dark w-full pl-11 pr-4 py-3 text-sm"
                      placeholder="•••••••••"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="input-dark w-full pl-11 pr-4 py-3 text-sm"
                      placeholder="•••••••••"
                    />
                  </div>
                </div>
              </>
            )}

            {error && (
              <div className="px-4 py-3 rounded-xl bg-error-100 border border-error-500/20 text-error-600 text-sm">
                {error}
              </div>
            )}

            {info && (
              <div className="px-4 py-3 rounded-xl bg-success-100 border border-success-500/20 text-success-600 text-sm">
                {info}
              </div>
            )}

            {mfaChallenge && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
                  Authenticator Code
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    maxLength={6}
                    required
                    className="input-dark w-full pl-11 pr-4 py-3 text-sm font-mono tracking-widest text-center"
                    placeholder="123456"
                  />
                </div>
              </div>
            )}

            {(!resetSent || recoveryActive) && (
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="animate-pulse">Processing...</span>
                ) : (
                  <>
                    {mfaChallenge ? 'Verify Code' : mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : recoveryActive ? 'Update Password' : 'Send Reset Link'}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </form>

          <div className="mt-6 text-center space-y-2">
            {mode === 'signin' && (
              <button
                onClick={() => { setMode('reset'); setError(null); setInfo(null); }}
                className="text-sm text-slate-500 hover:text-primary-600 transition-colors"
              >
                Forgot password?
              </button>
            )}
            <div>
              <button
                onClick={() => {
                  setMode(mode === 'signin' ? 'signup' : 'signin');
                  setError(null);
                  setInfo(null);
                }}
                className="text-sm text-slate-500 hover:text-primary-600 transition-colors"
              >
                {mode === 'signin'
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
            </div>
            {mode === 'reset' && (
              <button
                onClick={() => { setMode('signin'); setError(null); setInfo(null); }}
                className="text-sm text-slate-500 hover:text-primary-600 transition-colors"
              >
                Back to sign in
              </button>
            )}
          </div>
        </div>

        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-slate-300 font-mono">
            Aurelian Marketing Engine v2.0
          </p>
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors">
            <ArrowLeft className="h-3 w-3" />
            Back to Showcase
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
